/**
 * Phase 4.5 — Call Orchestrator Store
 *
 * Holds the live state of inbound calls for the IncomingCallsBanner (under the
 * top header) and the Sales Panel:
 *   - activeCalls:    array of all active calls (newest first)
 *   - dismissedCallIds: per-tab Set of call_log_ids the user closed via ×
 *   - expandedCallIds:  per-tab Set of call_log_ids whose accordion body is open
 *   - alertsMuted:    user pref (per-user/per-domain) loaded from
 *                     /panel/orchestrator/user-prefs/ — controls full banner vs.
 *                     thin muted strip
 *   - pendingCall:    a NEW incoming call awaiting Save&Switch / Discard / Stay
 *                     (only used in single-call mode; stack mode just appends)
 *   - lastEnrichment: cache of the latest enrichment payload for the form
 *
 * IMPORTANT: This store is INDEPENDENT from `incomingCallsStore` (Dialer V3).
 *            We never write to Dialer V3 state; we only listen to backend
 *            broadcasts and react in our own UI layer.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@/api/client'

const BASE = '/sales/panel/orchestrator'

export const useCallOrchestratorStore = defineStore('callOrchestrator', () => {
  // ─── Feature flag (per-domain, with optional per-user beta) ────────
  const enabled = ref(false)
  const canEditFlag = ref(false)
  const flagLoaded = ref(false)

  // ─── User preferences (per-user/per-domain, persisted in DB) ───────
  const alertsMuted = ref(false)
  const userPrefsLoaded = ref(false)

  // ─── Live call stack ───────────────────────────────────────────────
  const activeCalls = ref([])             // newest first
  const dismissedCallIds = ref(new Set()) // per-tab; cleared when call ends
  const expandedCallIds = ref(new Set())  // per-tab; survives only this session
  const pendingCall = ref(null)           // single-call switch flow only
  const lastEnrichment = ref(null)

  // ─── Switch UX (legacy single-call flow) ───────────────────────────
  const switchDialogOpen = ref(false)
  const dirtyFormsSnapshot = ref([])

  // ─── Connection diagnostics ────────────────────────────────────────
  const wsStatus = ref('idle')    // idle | connecting | connected | reconnecting | disabled
  const lastEventAt = ref(null)

  // ─── Computed ──────────────────────────────────────────────────────
  const visibleCalls = computed(() =>
    activeCalls.value.filter(c => !dismissedCallIds.value.has(c.call_log_id))
  )
  const visibleCount = computed(() => visibleCalls.value.length)
  const hiddenCount = computed(() =>
    activeCalls.value.length - visibleCalls.value.length
  )
  // currentCall = the topmost visible call. Kept as a computed so any
  // legacy code that reads `currentCall` keeps working transparently.
  const currentCall = computed(() => visibleCalls.value[0] || null)
  const hasActiveCall = computed(() => visibleCalls.value.length > 0)
  const hasAnyCall = computed(() => activeCalls.value.length > 0)
  const isInbound = computed(() => currentCall.value?.direction === 'inbound')
  const customerId = computed(() => currentCall.value?.enrichment?.customer_id || null)
  const customerName = computed(() => currentCall.value?.enrichment?.customer_name || null)
  const callState = computed(() => currentCall.value?.enrichment?.state || 'new_customer')
  const isFreeCheckup = computed(() => !!currentCall.value?.enrichment?.free_checkup_eligible)
  const stickyOwnerUserId = computed(() => currentCall.value?.enrichment?.sticky_owner_user_id || null)

  // ─── Actions: settings flag ────────────────────────────────────────

  async function loadFlag() {
    try {
      const r = await axios.get(`${BASE}/settings/`)
      enabled.value = !!r.data?.enabled
      canEditFlag.value = !!r.data?.can_edit
      flagLoaded.value = true
      return r.data
    } catch (e) {
      console.warn('[callOrchestrator] loadFlag failed:', e?.message || e)
      enabled.value = false
      flagLoaded.value = true
      return { enabled: false }
    }
  }

  async function setFlag(value) {
    const r = await axios.post(`${BASE}/settings/`, { enabled: !!value })
    enabled.value = !!r.data?.enabled
    return r.data
  }

  // ─── Actions: per-user preferences ─────────────────────────────────

  async function loadUserPrefs() {
    try {
      const r = await axios.get(`${BASE}/user-prefs/`)
      alertsMuted.value = !!r.data?.alerts_muted
    } catch (e) {
      console.warn('[callOrchestrator] loadUserPrefs failed:', e?.message || e)
      alertsMuted.value = false
    } finally {
      userPrefsLoaded.value = true
    }
  }

  async function saveUserPrefs() {
    try {
      await axios.post(`${BASE}/user-prefs/`, {
        alerts_muted: !!alertsMuted.value,
      })
    } catch (e) {
      console.warn('[callOrchestrator] saveUserPrefs failed:', e?.message || e)
    }
  }

  function toggleMute() {
    alertsMuted.value = !alertsMuted.value
    // Optimistic UI; persist in background. Failure is logged but does not
    // revert local state — the next loadUserPrefs() will reconcile.
    saveUserPrefs()
  }

  // ─── Actions: stack management ─────────────────────────────────────

  function dismissCall(callLogId) {
    if (!callLogId) return
    const next = new Set(dismissedCallIds.value)
    next.add(callLogId)
    dismissedCallIds.value = next
    // also collapse if it was expanded (free memory + no stale state)
    if (expandedCallIds.value.has(callLogId)) {
      const e = new Set(expandedCallIds.value)
      e.delete(callLogId)
      expandedCallIds.value = e
    }
  }

  function dismissAllCalls() {
    const next = new Set(dismissedCallIds.value)
    for (const c of activeCalls.value) next.add(c.call_log_id)
    dismissedCallIds.value = next
    expandedCallIds.value = new Set()
  }

  function toggleExpanded(callLogId) {
    if (!callLogId) return
    const next = new Set(expandedCallIds.value)
    if (next.has(callLogId)) next.delete(callLogId)
    else next.add(callLogId)
    expandedCallIds.value = next
  }

  function collapseAll() {
    expandedCallIds.value = new Set()
  }

  // ─── Reload-safe restore ───────────────────────────────────────────

  async function loadCurrentCall() {
    if (!enabled.value) return null
    try {
      const r = await axios.get(`${BASE}/current/`)
      if (r.data?.enabled && r.data?.call) {
        // Replace the stack with whatever the backend says is currently
        // active. This is a snapshot — live WS events extend it from here.
        activeCalls.value = [r.data.call]
        lastEnrichment.value = r.data.call.enrichment || null
        return r.data.call
      }
      return null
    } catch (e) {
      console.warn('[callOrchestrator] loadCurrentCall failed:', e?.message || e)
      return null
    }
  }

  // ─── Live event handler (multi-call aware) ─────────────────────────
  /**
   * Called by the WS composable when a new `sales.call_event` arrives.
   *
   * Multi-call semantics:
   *   - 'incoming' adds the call to the FRONT of the stack (newest on top).
   *     If the same call_log_id is already there, it is updated in place
   *     (e.g. enrichment arrived later).
   *   - 'connected' updates status in place.
   *   - 'ended' removes the call from the stack and frees its dismissed/
   *     expanded state.
   *
   * Single-call switch flow (legacy SwitchContextDialog) is preserved:
   * if there is currently exactly ONE visible call AND a brand-new incoming
   * arrives AND there are dirty forms, we still raise the switch dialog.
   * In multi-call mode (already 2+ visible) we just append silently.
   */
  function handleEvent(payload, dirtyForms = []) {
    lastEventAt.value = new Date().toISOString()

    const subtype = payload.subtype
    const callId = payload.call_log_id

    if (subtype === 'incoming') {
      const incoming = _normalizeCall(payload)

      // A fresh "incoming" for this id wipes any stale dismissed-state
      // for the same id (defensive — backend should not re-broadcast
      // 'incoming' for an already-running call, but if it does, the user
      // should see it again).
      if (dismissedCallIds.value.has(callId)) {
        const next = new Set(dismissedCallIds.value)
        next.delete(callId)
        dismissedCallIds.value = next
      }

      const idx = activeCalls.value.findIndex(c => c.call_log_id === callId)
      if (idx >= 0) {
        // update in place, preserve position in the stack
        const merged = { ...activeCalls.value[idx], ...incoming }
        const copy = activeCalls.value.slice()
        copy.splice(idx, 1, merged)
        activeCalls.value = copy
        lastEnrichment.value = merged.enrichment || lastEnrichment.value
        return
      }

      // New call — push to the top of the stack
      activeCalls.value = [incoming, ...activeCalls.value]
      lastEnrichment.value = incoming.enrichment || null

      // Single-call switch flow: only raise the dialog if BEFORE this call
      // there was exactly ONE visible call AND user has unsaved data.
      // (After unshift, visibleCalls.length is 2 here.)
      if (visibleCalls.value.length === 2 && dirtyForms.length) {
        pendingCall.value = incoming
        dirtyFormsSnapshot.value = dirtyForms.map(f => ({ id: f.id, label: f.label }))
        switchDialogOpen.value = true
      }
      return
    }

    if (subtype === 'connected') {
      const idx = activeCalls.value.findIndex(c => c.call_log_id === callId)
      if (idx >= 0) {
        const copy = activeCalls.value.slice()
        // Phase B1 — merge accepted_by_user_id along with status so the
        // Sales Panel watch can immediately tell who just answered.
        copy.splice(idx, 1, {
          ...copy[idx],
          status: payload.status,
          accepted_by_user_id: payload.accepted_by_user_id != null
            ? Number(payload.accepted_by_user_id)
            : copy[idx].accepted_by_user_id,
        })
        activeCalls.value = copy
      }
      return
    }

    if (subtype === 'ended') {
      activeCalls.value = activeCalls.value.filter(c => c.call_log_id !== callId)
      if (dismissedCallIds.value.has(callId)) {
        const d = new Set(dismissedCallIds.value)
        d.delete(callId)
        dismissedCallIds.value = d
      }
      if (expandedCallIds.value.has(callId)) {
        const e = new Set(expandedCallIds.value)
        e.delete(callId)
        expandedCallIds.value = e
      }
      if (pendingCall.value?.call_log_id === callId) {
        pendingCall.value = null
        switchDialogOpen.value = false
        dirtyFormsSnapshot.value = []
      }
      return
    }

    if (subtype === 'job_handoff') {
      const activeJob = payload?.enrichment?.active_job || null
      if (!activeJob?.job_id) return
      console.log('[callOrchestrator] job handoff event:', activeJob)
      activeCalls.value = activeCalls.value.map((call) => {
        const enrichment = call.enrichment || {}
        const currentJobId = enrichment.active_job?.job_id
        if (currentJobId && Number(currentJobId) === Number(activeJob.job_id)) {
          return {
            ...call,
            enrichment: {
              ...enrichment,
              state: 'dispatch_client',
              active_job: { ...enrichment.active_job, ...activeJob },
            },
          }
        }
        return call
      })
      lastEnrichment.value = {
        ...(lastEnrichment.value || {}),
        state: 'dispatch_client',
        active_job: activeJob,
      }
      window.dispatchEvent(new CustomEvent('sales-panel-job-handoff', { detail: activeJob }))
      return
    }
  }

  // ─── SwitchContextDialog actions (legacy single-call UX) ───────────
  function acceptSwitch() {
    if (!pendingCall.value) return
    // Drop the previous top so the pendingCall becomes the visible top
    const top = visibleCalls.value[0]
    if (top && top.call_log_id !== pendingCall.value.call_log_id) {
      dismissCall(top.call_log_id)
    }
    lastEnrichment.value = pendingCall.value.enrichment || null
    pendingCall.value = null
    switchDialogOpen.value = false
    dirtyFormsSnapshot.value = []
  }

  function rejectSwitch() {
    // Discard the pending call from the stack (user chose Stay)
    if (pendingCall.value) {
      activeCalls.value = activeCalls.value.filter(
        c => c.call_log_id !== pendingCall.value.call_log_id
      )
    }
    pendingCall.value = null
    switchDialogOpen.value = false
    dirtyFormsSnapshot.value = []
  }

  function endCall() {
    // Force-clear the entire stack (used by manual cleanup paths).
    activeCalls.value = []
    dismissedCallIds.value = new Set()
    expandedCallIds.value = new Set()
    lastEnrichment.value = null
  }

  function setWsStatus(s) { wsStatus.value = s }

  // ─── Helpers ───────────────────────────────────────────────────────
  function _normalizeCall(p) {
    return {
      call_log_id: p.call_log_id,
      sip_uuid: p.sip_uuid || null,
      phone: p.phone || null,
      twilio_number: p.twilio_number || null,
      direction: p.direction || 'inbound',
      status: p.status || 'ringing',
      caller_name: p.caller_name || null,
      created_at: p.created_at || new Date().toISOString(),
      enrichment: p.enrichment || {},
      // Phase B1 — owner of the call (null while still ringing). Sales
      // Panel uses this to ignore calls that another operator just picked
      // up so the panel doesn't auto-switch off the current customer.
      accepted_by_user_id: p.accepted_by_user_id != null
        ? Number(p.accepted_by_user_id)
        : null,
    }
  }

  return {
    // state
    enabled, canEditFlag, flagLoaded,
    alertsMuted, userPrefsLoaded,
    activeCalls, dismissedCallIds, expandedCallIds,
    pendingCall, lastEnrichment,
    switchDialogOpen, dirtyFormsSnapshot,
    wsStatus, lastEventAt,
    // computed
    visibleCalls, visibleCount, hiddenCount,
    currentCall, hasActiveCall, hasAnyCall,
    isInbound, customerId, customerName,
    callState, isFreeCheckup, stickyOwnerUserId,
    // actions
    loadFlag, setFlag,
    loadUserPrefs, saveUserPrefs, toggleMute,
    dismissCall, dismissAllCalls, toggleExpanded, collapseAll,
    loadCurrentCall,
    handleEvent, acceptSwitch, rejectSwitch, endCall, setWsStatus,
  }
})
